// Bella Vita frontend — application entry.
//
// Boot order:
//   1. apply persisted language to <html lang/dir>
//   2. validate the active backend adapter (throws early if misconfigured)
//   3. define the route table (preserves the design-v2 page inventory)
//   4. start the History-API router
//
// Owner rules in force: native Odoo customer account at confirmation, single salon (no branch route),
// Arabic primary, mock adapter only this phase. See PROJECT_RULES.md.

import './styles/index.css';
import './store/booking.js';
import { applyDocumentLang, t } from './locale/index.js';
import { getAdapter } from './services/index.js';
import { mountToastHost, toast } from './components/toast.js';
import { defineRoutes, setNotFound, startRouter, navigate } from './router.js';

// 1. persisted language -> <html lang/dir>
applyDocumentLang();

// 2. validate the active backend adapter (throws early if misconfigured)
getAdapter();

// 3. global toast host (idempotent)
mountToastHost();

// 4. last-resort safety net: surface unexpected runtime failures instead of
//    failing silently. Per-view failures are already handled by the router and
//    by loadInto(); this only catches the truly unhandled.
window.addEventListener('unhandledrejection', (e) => {
  if (import.meta.env?.DEV) console.error('[unhandledrejection]', e.reason);
  toast(t('common.error_generic'));
});
window.addEventListener('error', (e) => {
  if (import.meta.env?.DEV) console.error('[error]', e.error || e.message);
});

// ---- Route table -----------------------------------------------------------
defineRoutes([
  { path: '/',                       load: () => import('./screens/home.js') },

  { path: '/services',               load: () => import('./screens/services.js') },
  { path: '/services/:serviceId',    load: () => import('./screens/service-detail.js') },

  // Booking wizard — one salon, no branch step. /book redirects to first step.
  { path: '/book',                   load: () => Promise.resolve({ default: () => { navigate('/book/services', { replace: true }); return document.createElement('div'); } }) },
  // Explicit provider-return route must precede /book/:step so an external
  // PSP redirect lands on the confirmation screen, not a booking wizard step.
  { path: '/book/confirmation',      load: () => import('./screens/confirmation.js') },
  { path: '/book/:step',             load: () => import('./screens/booking/index.js') },

  { path: '/checkout',               load: () => import('./screens/checkout.js') },
  { path: '/payment',                load: () => import('./screens/payment.js') },
  { path: '/confirmation',           load: () => import('./screens/confirmation.js') },

  // Customer identity/account pages intentionally do not live in this SPA.
  // Odoo Portal + auth_signup own /my, /web/login, /web/signup and password
  // reset. Historical SPA account/auth URLs are redirected by Nginx only.
]);

setNotFound(() => import('./screens/notfound.js'));

startRouter({ mount: '#bv-app' });
